import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import ConnectionModel, {
  ConnectionKind,
  ConnectionResponseType,
} from '@mm/state/model/ConnectionModel';
import {
  Heading,
  Input,
  Button,
  CollapsibleContentContainer,
  ThreeDotMenu,
  JSONEditor,
} from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import {
  ConnectionVariableKind,
  ConnectionVariableModel,
} from '@mm/state/model/ConnectionVariableModel';
import { ConnectionTagModel } from '@mm/state/model/ConnectionTagModel';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { isEqual, isEmpty } from 'lodash';
import { colors } from '@mm/utils/src/colors';

import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './EditJSONWebhookIntegrationSlideoutEditor.module.css';

import { useEditConnectionActions } from '@mm/state/hooks/use-edit-connections-actions';
import { EditConnectionAttributes } from '@mm/stateful-components/EditConnectionPanel/EditConnectionAttributes/EditConnectionAttributes';
import PrimarySelect from '@mm/components/legacy-components/PrimarySelect/PrimarySelect';
import { EditResponseType } from '@mm/stateful-components/EditConnectionPanel/EditResponseType/EditResponseType';
import { EditResponseMapping } from '@mm/stateful-components/EditConnectionPanel/EditResponseMapping/EditResponseMapping';
import EditConnectionType from '../../../../features/connections/panels/EditConnectionPanel/EditConnectionType/EditConnectionType';
import EditRequestTypeAndURL from '../../../../features/connections/panels/EditConnectionPanel/EditRequestTypeAndURL/EditRequestTypeAndURL';
import { Switch } from 'antd';
import {
  blankVariable,
  canBeTested,
  tagsFormatOptions,
} from '@mm/state/helpers/connection-helpers';
import VariableHelpers from '@mm/state/helpers/variable-helpers';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { JSONWebhookTemplates } from './JSONWebhookTemplates';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export interface EditJSONWebhookIntegrationSlideoutEditorProps {
  webhookId: number;
  deleteAction?: () => Promise<void>;
}

// tslint:disable-next-line:cyclomatic-complexity
export const EditJSONWebhookIntegrationSlideoutEditor = ({
  webhookId,
  deleteAction,
}: EditJSONWebhookIntegrationSlideoutEditorProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const viewActions = useViewActions();
  const modalActions = useModalActions();
  const { retrieveById, retrieveWidgetConnections, deleteConnection } =
    useConnectionCRUDActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const [connection, setConnection] = useState<ConnectionModel>();
  const [descriptionEditMode, setDescriptionEditMode] = useState(false);
  const [nameEditMode, setNameEditMode] = useState(false);
  const { setRef } = useSlideoutOnClose();
  const [localDescription, setLocalDescription] = useState(
    connection?.description || '',
  );
  const [localName, setLocalName] = useState(connection?.name || '');
  const [error, setError] = useState('');
  const tagsFormatValue = useMemo(
    () => (connection?.tagsField ? tagsFormatOptions[1] : tagsFormatOptions[0]),
    [connection?.tagsField],
  );
  const [variablesToSend, setVariablesToSend] = useState<
    ConnectionVariableModel[]
  >([]);
  const [variablesToReceive, setVariablesToReceive] = useState<
    ConnectionVariableModel[]
  >([]);
  const { updateRemoteConnection } = useEditConnectionActions();
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const [jsonEditorVisible, setJsonEditorVisible] = useState(
    !!connection?.requestBody || !!connection?.requestHeaders || false,
  );

  const toggleAdvancedJSONEditor = async () => {
    if (jsonEditorVisible) {
      await updateConnection({ requestHeaders: {}, requestBody: {} });
    }

    setJsonEditorVisible(!jsonEditorVisible);
  };

  const parsedJsonConnection = (retrievedConnection: ConnectionModel) => {
    const parsedBody =
      typeof retrievedConnection?.requestBody === 'string'
        ? JSON.parse(retrievedConnection.requestBody)
        : retrievedConnection?.requestBody;
    const parsedHeaders =
      typeof retrievedConnection?.requestHeaders === 'string'
        ? JSON.parse(retrievedConnection.requestHeaders)
        : retrievedConnection?.requestHeaders;
    return {
      ...retrievedConnection,
      requestBody: parsedBody,
      requestHeaders: parsedHeaders,
    };
  };

  useEffect(() => {
    const getConnection = async () => {
      if (activeFbPageId && webhookId) {
        const retrievedConnection = await retrieveById(
          activeFbPageId,
          webhookId,
        );
        const newConnection =
          retrievedConnection && parsedJsonConnection(retrievedConnection);
        const connectionVariables = newConnection?.variables || [];
        const variablesToReceive = connectionVariables.filter(
          (variable: ConnectionVariableModel) =>
            variable.kind === ConnectionVariableKind.toReceive,
        );
        const variablesToSend = connectionVariables.filter(
          (variable: ConnectionVariableModel) =>
            variable.kind !== ConnectionVariableKind.toReceive,
        );
        const initialVariablesToReceive =
          variablesToReceive.length > 0
            ? variablesToReceive
            : [blankVariable(ConnectionVariableKind.toReceive)];

        setVariablesToSend(variablesToSend);
        setVariablesToReceive(initialVariablesToReceive);
        setConnection(newConnection);
        setJsonEditorVisible(
          !isEmpty(newConnection?.requestBody || {}) ||
            !isEmpty(newConnection?.requestHeaders || {}),
        );
        setLocalDescription(newConnection?.description || '');
        setLocalName(newConnection?.name || '');
      }
    };
    getConnection();
  }, [activeFbPageId, webhookId]);

  const validateJSONPayload = useCallback(
    (data, connection): Record<any, any> => {
      const k = Object.entries(data).find((k, v) => !v)?.[0] || '';

      if (data[k] instanceof Error) {
        return {};
      }

      if (isEqual(data[k], connection[k])) {
        return {};
      }

      return data;
    },
    [connection],
  );

  const handleJSONFieldsSave = async data => {
    if (!jsonEditorVisible) {
      return;
    }

    const jsonFields = validateJSONPayload(data, connection);

    if (!Object.values(jsonFields).length) {
      return;
    }

    await updateConnection(jsonFields);
  };

  const updateConnection = async (updatedFields: Partial<ConnectionModel>) => {
    if (!connection) {
      return;
    }

    const connectionWithUpdatedFields = {
      ...connection,
      ...updatedFields,
    };

    const updatedConnection = await updateRemoteConnection({
      ...connectionWithUpdatedFields,
      requestHeaders: JSON.stringify(
        connectionWithUpdatedFields.requestHeaders,
      ),
      requestBody: JSON.stringify(connectionWithUpdatedFields.requestBody),
    });

    if (updatedConnection) {
      const connectionVariables = updatedConnection.variables || [];
      const variablesToSend = connectionVariables.filter(
        (variable: ConnectionVariableModel) =>
          variable.kind !== ConnectionVariableKind.toReceive,
      );

      setVariablesToSend(variablesToSend);
      setConnection(parsedJsonConnection(updatedConnection));
    }
  };
  const onClickSaveDescription = () => {
    updateConnection({ description: localDescription || '' });
    setDescriptionEditMode(false);
  };

  const onClickCancelSaveDescription = () => {
    setLocalDescription(connection?.description || '');
    setDescriptionEditMode(false);
  };

  const onClickSaveName = () => {
    updateConnection({ name: localName || '' });
    setNameEditMode(false);
  };

  const onClickCancelSaveName = () => {
    setLocalName(connection?.name || '');
    setNameEditMode(false);
  };
  const onTagsChange = (tags: ConnectionTagModel[]) =>
    updateConnection({ tags });

  const onClose = async () => {
    await retrieveWidgetConnections(activeFbPageId);
    viewActions.hideSlideoutMenu();
  };

  const onStatusChange = async () => {
    updateConnection({ active: !connection?.active });
  };

  const onClickDelete = () => {
    const deleteCallback = async () => {
      if (connection) {
        await deleteAction?.();
        setTimeout(async () => {
          await deleteConnection(activeFbPageId, connection);
          await retrieveWidgetConnections(activeFbPageId);
          viewActions.hideSlideoutMenu();
        }, 300);
      }
    };

    modalActions.openDeleteConfirmModal({
      headerText: 'Are you sure you want to delete this connection?',
      deleteCallback,
      newStyle: true,
    });
  };

  const onNewMappingFound = (pathNames: string[]) => {
    setVariablesToReceive([
      ...variablesToReceive,
      ...pathNames.map(name => ({
        ...blankVariable(ConnectionVariableKind.toReceive),
        receivedAs: name,
      })),
    ]);
  };

  const onClickTestConnection = () => {
    if (!connection) {
      return;
    }
    const { url } = connection;
    const urlVariables = VariableHelpers.getVariablesFromString(url);

    modalActions.openTestConnectionModal({
      botId: chatbotId,
      connection,
      connectedPageId: activeFbPageId || -1,
      url,
      variables: variablesToSend,
      urlVariables,
      mappedVariables: variablesToReceive,
      onNewMappingFound,
      shouldMapResponse:
        connection.responseType === ConnectionResponseType.SAVE_ATTRIBUTES,
    });
  };

  const canUseJSONEditor = true; // !!features['custom_json'];

  const selectAction = (action: string) => {
    switch (action) {
      case 'delete': {
        return onClickDelete();
      }
      default: {
        return null;
      }
    }
  };

  if (!connection) {
    return null;
  }

  const { tags = [] } = connection;

  const actionsMenu = [
    {
      disabled: false,
      name: 'Delete',
      value: 'delete',

      children: (
        <div className="connection-table__action-cell-item">
          <div className="connection-table__action-cell-item_icon">
            <DeleteOutlined />
          </div>
          <div>Delete</div>
        </div>
      ),
      hideName: true,
    },
  ];

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        {nameEditMode ? (
          <Input
            value={localName}
            onChange={e => setLocalName(e.target.value)}
            disabled={!nameEditMode}
            placeholder="Connection Name"
            maxLength={50}
            css={{
              container: classnames(styles.descriptionInput, {
                [styles.descriptionInputDisabledMode]: !descriptionEditMode,
              }),
            }}
          />
        ) : (
          <Heading className={styles.headerTitle} size={3}>
            {connection.name}
          </Heading>
        )}

        {!nameEditMode && (
          <EditOutlined
            onClick={() => setNameEditMode(true)}
            className={styles.editIcon}
          />
        )}

        {nameEditMode && (
          <>
            <div onClick={onClickSaveName} className={styles.saveBtn}>
              <CheckOutlined className={styles.checkmark} />
            </div>
            <CloseOutlined
              className={styles.closeIcon}
              onClick={onClickCancelSaveName}
            />
          </>
        )}
        {!nameEditMode && (
          <div className={styles.headerButtons}>
            <Button
              color="secondary-blue"
              css={{ container: styles.testConnectionBtn }}
              onClick={onClickTestConnection}
              disabled={!canBeTested(connection)}
            >
              Test It
            </Button>
            <Button
              color={connection.url ? undefined : 'blue'}
              onClick={onStatusChange}
              css={{
                container: connection.url
                  ? styles.Button
                  : styles.testConnectionBtn,
              }}
              disabled={!connection.url}
            >
              {!connection.active ? 'Activate' : 'Pause'}
            </Button>
            <ThreeDotMenu
              elements={actionsMenu}
              selectItem={selectAction}
              className={styles.dropdown}
            />
            <CloseOutlined className={styles.closeIcon} onClick={onClose} />
          </div>
        )}
      </div>
      <div className={styles.descriptionArea}>
        {descriptionEditMode ? (
          <Input
            value={localDescription}
            onChange={e => setLocalDescription(e.target.value)}
            disabled={!descriptionEditMode}
            placeholder="Description text"
            maxLength={50}
            css={{
              container: classnames(styles.descriptionInput, {
                [styles.descriptionInputDisabledMode]: !descriptionEditMode,
              }),
            }}
          />
        ) : (
          <div className={styles.descriptionPreview}>
            {localDescription || 'Description text'}
          </div>
        )}

        {!descriptionEditMode && (
          <EditOutlined
            onClick={() => setDescriptionEditMode(true)}
            className={styles.editIcon}
          />
        )}

        {descriptionEditMode && (
          <>
            <div onClick={onClickSaveDescription} className={styles.saveBtn}>
              <CheckOutlined className={styles.checkmark} />
            </div>
            <CloseOutlined
              className={styles.closeIcon}
              onClick={onClickCancelSaveDescription}
            />
          </>
        )}
      </div>
      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              ordinal="1"
              title="Connection Settings"
              color={colors.blue}
              css={{
                container: styles.collapsibleContainerLast,
              }}
            >
              <div className={styles.title}>What would you like to do?</div>
              <EditConnectionType
                useRowStyle
                connection={connection}
                chatbotId={chatbotId}
                isSlideout={false}
                onAudienceChange={audienceId =>
                  updateConnection({ audienceId })
                }
                onCancel={() => updateConnection({ audienceId: null })}
                onKindChange={kind => {
                  let { responseType } = connection;
                  if (
                    kind === ConnectionKind.AUDIENCE &&
                    responseType === ConnectionResponseType.MESSENGER_JSON
                  ) {
                    responseType = ConnectionResponseType.NONE;
                  }
                  updateConnection({ kind, responseType });
                }}
              />
              <div className="edit-connection-panel__devider" />
              <EditRequestTypeAndURL
                url={connection.url}
                requestType={connection.requestType}
                onRequestTypeChange={requestType =>
                  updateConnection({ requestType })
                }
                onUrlChange={url => updateConnection({ url })}
              />
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              ordinal="2"
              title="Include attributes as form data"
              color={colors.blue}
              css={{ container: styles.collapsibleContainerLast }}
            >
              <div className="edit-connection-panel__attributes-settings">
                {canUseJSONEditor && (
                  <>
                    <JSONWebhookTemplates
                      connection={connection}
                      updateConnection={updateConnection}
                      setJsonEditorVisible={setJsonEditorVisible}
                    />
                    <span
                      onClick={toggleAdvancedJSONEditor}
                      className={styles.jsonSwitchContainer}
                    >
                      <Switch
                        checked={!!jsonEditorVisible}
                        size="small"
                        className={styles.jsonSwitch}
                        onChange={toggleAdvancedJSONEditor}
                      />
                      Advanced JSON Editor
                    </span>
                  </>
                )}

                {jsonEditorVisible ? (
                  <div className={styles.jsonEditors}>
                    <Heading text="Headers" isBold size={5} />

                    <JSONEditor
                      defaultValue={connection.requestHeaders || {}}
                      onBlur={data => {
                        handleJSONFieldsSave({ requestHeaders: data });
                      }}
                    />

                    <Heading text="Body" isBold size={5} />
                    <JSONEditor
                      defaultValue={connection.requestBody || {}}
                      onBlur={data =>
                        handleJSONFieldsSave({ requestBody: data })
                      }
                    />
                  </div>
                ) : (
                  <EditConnectionAttributes
                    tags={tags}
                    variablesToSend={variablesToSend}
                    facebookPageId={activeFbPageId}
                    onVariablesChange={nextVariablesToSend =>
                      updateConnection({
                        variables: [
                          ...nextVariablesToSend,
                          ...variablesToReceive,
                        ],
                      })
                    }
                    onTagsChange={onTagsChange}
                  />
                )}

                <div className="edit-connection-panel__devider" />

                <div className="edit-connection-panel__format">
                  {!!connection.tags?.length && (
                    <div className="edit-connection-panel__option">
                      <div className="edit-connection-panel__title">
                        How tags should be sent
                      </div>
                      <PrimarySelect
                        options={tagsFormatOptions}
                        value={tagsFormatValue}
                        onChange={opt => {
                          updateConnection({
                            tagsField: opt.id === 'array' ? 'tags' : null,
                          });
                          setError('');
                        }}
                        customDropdownStyles={{ zIndex: '1000003' }}
                      />
                    </div>
                  )}
                  {tagsFormatValue.id === 'array' && (
                    <div className="edit-connection-panel__option">
                      <div className="edit-connection-panel__title">Name</div>
                      <Input
                        onChange={e => {
                          setError(
                            e.target.value
                              ? ''
                              : 'Tag field name should not be empty',
                          );
                        }}
                        onBlur={e => {
                          const { value } = e?.target;

                          if (!value) {
                            return;
                          }

                          updateConnection({
                            tagsField: e?.target.value,
                          });
                        }}
                        value={connection.tagsField || undefined}
                      />
                      {error && (
                        <span className="edit-connection-panel__option-error">
                          {error}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="edit-connection-panel__options">
                  <EditResponseType
                    connection={connection}
                    onResponseTypeChange={responseType =>
                      updateConnection({ responseType })
                    }
                    customOptionsPosition="top"
                  />
                </div>
              </div>
            </CollapsibleContentContainer>
            {connection.responseType ===
              ConnectionResponseType.SAVE_ATTRIBUTES && (
              <CollapsibleContentContainer
                ordinal="3"
                title={`Map Response data to ${companyName} attributes`}
                color={colors.blue}
                css={{ container: styles.collapsibleContainerLast }}
              >
                <EditResponseMapping
                  variablesToReceive={variablesToReceive}
                  onVariablesChange={nextVariablesToReceive => {
                    setVariablesToReceive(nextVariablesToReceive);
                    updateConnection({
                      variables: [
                        ...nextVariablesToReceive,
                        ...variablesToSend,
                      ],
                    });
                  }}
                  canBeTested={canBeTested(connection)}
                  customOptionsPosition="top"
                />
              </CollapsibleContentContainer>
            )}
          </div>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
